import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  HomeModernIcon,
  UserCircleIcon,
  CalendarDaysIcon,
  Cog6ToothIcon,
  ClipboardDocumentListIcon,
  BanknotesIcon,
} from '@heroicons/react/24/outline';
import { Role, useAuth } from '../state/AuthContext';

interface NavItem {
  name: string;
  to: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  roles: Role[];
  badge?: string;
}

const navItems: NavItem[] = [
  {
    name: 'Dashboard',
    to: '/',
    icon: HomeModernIcon,
    roles: ['medical_director', 'surgery', 'internal_med', 'customer_service', 'billing'],
  },
  {
    name: 'Patients',
    to: '/patients',
    icon: UserCircleIcon,
    roles: ['medical_director', 'surgery', 'internal_med'],
  },
  {
    name: 'Care plans',
    to: '/care',
    icon: ClipboardDocumentListIcon,
    roles: ['medical_director', 'surgery', 'internal_med'],
    badge: 'Rounds',
  },
  {
    name: 'Appointments',
    to: '/appointments',
    icon: CalendarDaysIcon,
    roles: ['medical_director', 'surgery', 'internal_med', 'customer_service'],
  },
  {
    name: 'Billing',
    to: '/billing',
    icon: BanknotesIcon,
    roles: ['medical_director', 'billing', 'customer_service'],
    badge: 'Finance',
  },
  {
    name: 'Audit trail',
    to: '/audit',
    icon: ClipboardDocumentListIcon,
    roles: ['medical_director', 'billing'],
    badge: 'Compliance',
  },
  {
    name: 'Settings',
    to: '/settings',
    icon: Cog6ToothIcon,
    roles: ['medical_director'],
  },
];

const Sidebar: React.FC = () => {
  const location = useLocation();
  const { role, roleMeta } = useAuth();

  return (
    <aside className="hidden w-72 border-r border-white/60 bg-white/80 px-4 py-6 backdrop-blur-xl lg:block">
      <div className="flex items-center justify-between gap-2 px-2">
        <div className="flex items-center gap-2">
          <span className="rounded-2xl bg-gradient-to-br from-brand-blue to-brand-indigo p-2 text-white shadow-soft">
            <HomeModernIcon className="h-6 w-6" />
          </span>
          <div>
            <p className="text-sm font-semibold text-slate-500">LKC Medical</p>
            <h2 className="text-lg font-semibold text-slate-900">Animal Hospital</h2>
          </div>
        </div>
        <div className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-500">
          {roleMeta?.label ?? 'Team member'}
        </div>
      </div>

      <nav className="mt-10 flex flex-col gap-1 px-2">
        {navItems.map((item) => {
          const isActive =
            item.to === '/' ? location.pathname === '/' : location.pathname.startsWith(item.to);
          const Icon = item.icon;
          const isAllowed = role ? item.roles.includes(role) : false;
          return (
            <Link
              key={item.name}
              to={isAllowed ? item.to : '#'}
              className={`group flex items-center justify-between rounded-xl px-3 py-2 text-sm font-medium transition ${
                isActive
                  ? 'bg-gradient-to-r from-brand-blue to-brand-sky text-white shadow-soft'
                  : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900'
              } ${isAllowed ? '' : 'pointer-events-none opacity-50'}`}
            >
              <span className="flex items-center gap-3">
                <Icon className="h-5 w-5" />
                {item.name}
              </span>
              {item.badge && (
                <span className="rounded-full bg-white/80 px-2 py-0.5 text-[10px] font-semibold text-slate-500">
                  {item.badge}
                </span>
              )}
            </Link>
          );
        })}
      </nav>

      <div className="mt-10 rounded-2xl border border-slate-200 bg-slate-900 p-4 text-white shadow-soft">
        <p className="text-sm font-semibold">Quick Insight</p>
        <p className="mt-2 text-xs text-white/80">
          24 patients scheduled today, 4 await triage review. Keep your team in sync.
        </p>
        <button
          type="button"
          className="mt-4 w-full rounded-xl bg-white/90 py-2 text-sm font-semibold text-slate-900 shadow"
        >
          View schedule
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
